import { isValid, parseISO } from 'date-fns';
import type {
  LaunchStatus,
  SavedCreativeFilters,
  SavedCreativeFiltersAppEntry,
  SavedCreativeFiltersStorage,
  HypothesisCreativesViewState,
  UploadedFilter,
} from '../types/hypothesis';
import {
  DEFAULT_SAVED_CREATIVE_FILTERS,
  DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE,
  LAUNCH_STATUS_OPTIONS,
  UPLOADED_FILTER_OPTIONS,
} from '../types/hypothesis';

export const SAVED_CREATIVE_FILTERS_STORAGE_KEY = 'fb_hypothesis_creatives_filters';

const normalizeSavedAt = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed.toISOString() : null;
};

export const formatSavedFilterAtLabel = (): string => 'Saved filter';

const UPLOADED_VALUES = new Set<UploadedFilter>(
  UPLOADED_FILTER_OPTIONS.map((option) => option.value),
);
const STATUS_VALUES = new Set<LaunchStatus>(
  LAUNCH_STATUS_OPTIONS.map((option) => option.value),
);

const sortStrings = (values: string[]): string[] =>
  [...values].map((value) => value.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b));

const isUploadedFilter = (value: unknown): value is UploadedFilter =>
  typeof value === 'string' && UPLOADED_VALUES.has(value as UploadedFilter);

const isLaunchStatus = (value: unknown): value is LaunchStatus =>
  typeof value === 'string' && STATUS_VALUES.has(value as LaunchStatus);

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return sortStrings(value.filter((item): item is string => typeof item === 'string'));
};

export const normalizeSavedCreativeFilters = (
  value: Partial<SavedCreativeFilters> | null | undefined,
): SavedCreativeFilters => ({
  search: typeof value?.search === 'string' ? value.search : '',
  uploaded: isUploadedFilter(value?.uploaded)
    ? value.uploaded
    : DEFAULT_SAVED_CREATIVE_FILTERS.uploaded,
  hypothesis: normalizeStringArray(value?.hypothesis),
  flow: normalizeStringArray(value?.flow),
  customer:
    typeof value?.customer === 'string' && value.customer.trim()
      ? value.customer
      : null,
  technology: normalizeStringArray(value?.technology),
  tpcFinishDate:
    typeof value?.tpcFinishDate === 'string' && value.tpcFinishDate.trim()
      ? value.tpcFinishDate
      : null,
  status: isLaunchStatus(value?.status) ? value.status : null,
});

export const pickSavedCreativeFilters = (
  state: Pick<
    HypothesisCreativesViewState,
    | 'search'
    | 'uploaded'
    | 'hypothesis'
    | 'flow'
    | 'customer'
    | 'technology'
    | 'tpcFinishDate'
    | 'status'
  >,
): SavedCreativeFilters =>
  normalizeSavedCreativeFilters({
    search: state.search,
    uploaded: state.uploaded,
    hypothesis: state.hypothesis,
    flow: state.flow,
    customer: state.customer,
    technology: state.technology,
    tpcFinishDate: state.tpcFinishDate,
    status: state.status,
  });

export const areSavedCreativeFiltersEqual = (
  a: SavedCreativeFilters,
  b: SavedCreativeFilters,
): boolean => {
  const left = normalizeSavedCreativeFilters(a);
  const right = normalizeSavedCreativeFilters(b);

  return (
    left.search === right.search &&
    left.uploaded === right.uploaded &&
    left.customer === right.customer &&
    left.tpcFinishDate === right.tpcFinishDate &&
    left.status === right.status &&
    left.hypothesis.length === right.hypothesis.length &&
    left.hypothesis.every((value, index) => value === right.hypothesis[index]) &&
    left.flow.length === right.flow.length &&
    left.flow.every((value, index) => value === right.flow[index]) &&
    left.technology.length === right.technology.length &&
    left.technology.every((value, index) => value === right.technology[index])
  );
};

/**
 * Remove saved filter values from the current filters.
 * Extra values the user added on top of the saved snapshot are kept.
 */
export const subtractSavedCreativeFilters = (
  current: SavedCreativeFilters,
  saved: SavedCreativeFilters,
): SavedCreativeFilters => {
  const left = normalizeSavedCreativeFilters(current);
  const right = normalizeSavedCreativeFilters(saved);

  const subtractArray = (values: string[], savedValues: string[]): string[] => {
    const savedSet = new Set(savedValues);
    return values.filter((value) => !savedSet.has(value));
  };

  return normalizeSavedCreativeFilters({
    search: left.search === right.search ? '' : left.search,
    uploaded:
      left.uploaded === right.uploaded
        ? DEFAULT_SAVED_CREATIVE_FILTERS.uploaded
        : left.uploaded,
    hypothesis: subtractArray(left.hypothesis, right.hypothesis),
    flow: subtractArray(left.flow, right.flow),
    customer: left.customer === right.customer ? null : left.customer,
    technology: subtractArray(left.technology, right.technology),
    tpcFinishDate:
      left.tpcFinishDate === right.tpcFinishDate ? null : left.tpcFinishDate,
    status: left.status === right.status ? null : left.status,
  });
};

const emptyStorage = (): SavedCreativeFiltersStorage => ({
  version: 1,
  byApp: {},
});

export const loadSavedCreativeFiltersStorage = (): SavedCreativeFiltersStorage => {
  try {
    const raw = localStorage.getItem(SAVED_CREATIVE_FILTERS_STORAGE_KEY);
    if (!raw) return emptyStorage();

    const parsed = JSON.parse(raw) as Partial<SavedCreativeFiltersStorage>;
    if (parsed.version !== 1 || !parsed.byApp || typeof parsed.byApp !== 'object') {
      return emptyStorage();
    }

    const byApp: Record<string, SavedCreativeFiltersAppEntry> = {};
    for (const [app, entry] of Object.entries(parsed.byApp)) {
      if (!entry || typeof entry !== 'object') continue;
      const savedAt = normalizeSavedAt(
        (entry as Partial<SavedCreativeFiltersAppEntry>).savedAt,
      );
      byApp[app] = {
        enabled: Boolean(entry.enabled),
        filters: normalizeSavedCreativeFilters(entry.filters),
        // Legacy entries (pre-savedAt) keep an empty string until next save/update.
        savedAt: savedAt ?? '',
      };
    }

    return { version: 1, byApp };
  } catch {
    return emptyStorage();
  }
};

export const saveSavedCreativeFiltersStorage = (
  storage: SavedCreativeFiltersStorage,
): void => {
  localStorage.setItem(SAVED_CREATIVE_FILTERS_STORAGE_KEY, JSON.stringify(storage));
};

export const getSavedCreativeFiltersEntry = (
  app: string,
): SavedCreativeFiltersAppEntry | null => {
  if (!app) return null;
  return loadSavedCreativeFiltersStorage().byApp[app] ?? null;
};

export const setSavedCreativeFiltersEntry = (
  app: string,
  entry: SavedCreativeFiltersAppEntry,
): void => {
  if (!app) return;
  const storage = loadSavedCreativeFiltersStorage();
  storage.byApp[app] = {
    enabled: entry.enabled,
    filters: normalizeSavedCreativeFilters(entry.filters),
    savedAt: normalizeSavedAt(entry.savedAt) ?? new Date().toISOString(),
  };
  saveSavedCreativeFiltersStorage(storage);
};

/** Toggle enabled without changing the saved filter snapshot. */
export const setSavedCreativeFiltersEnabled = (
  app: string,
  enabled: boolean,
): void => {
  if (!app) return;
  const entry = getSavedCreativeFiltersEntry(app);
  if (!entry) return;
  setSavedCreativeFiltersEntry(app, { ...entry, enabled });
};

export const clearSavedCreativeFiltersEntry = (app: string): void => {
  if (!app) return;
  const storage = loadSavedCreativeFiltersStorage();
  if (!(app in storage.byApp)) return;

  const byApp = Object.fromEntries(
    Object.entries(storage.byApp).filter(([key]) => key !== app),
  );
  saveSavedCreativeFiltersStorage({ version: 1, byApp });
};

export const resolveHypothesisCreativesViewState = (
  app: string | undefined | null,
  base: HypothesisCreativesViewState = DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE,
): HypothesisCreativesViewState => {
  if (!app) return base;
  const entry = getSavedCreativeFiltersEntry(app);
  if (!entry?.enabled) return base;
  return {
    ...base,
    ...normalizeSavedCreativeFilters(entry.filters),
  };
};

const uploadedLabel = (value: UploadedFilter): string =>
  UPLOADED_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? value;

const statusLabel = (value: LaunchStatus): string =>
  LAUNCH_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;

/** Human-readable name/value rows for the clear-confirm dialog. */
export interface SavedCreativeFilterSummaryRow {
  name: string;
  value: string;
}

interface SummaryFieldConfig<K extends keyof SavedCreativeFilters> {
  key: K;
  name: string;
  format: (value: SavedCreativeFilters[K]) => string | null;
}

const joinNonEmpty = (values: string[]): string | null =>
  values.length > 0 ? values.join(', ') : null;

const SUMMARY_FIELDS = [
  {
    key: 'search',
    name: 'Search',
    format: (value) => {
      const trimmed = value.trim();
      return trimmed || null;
    },
  },
  {
    key: 'uploaded',
    name: 'Uploaded',
    format: (value) => uploadedLabel(value),
  },
  {
    key: 'hypothesis',
    name: 'Hypothesis',
    format: joinNonEmpty,
  },
  {
    key: 'flow',
    name: 'Flow',
    format: joinNonEmpty,
  },
  {
    key: 'customer',
    name: 'Customer',
    format: (value) => value,
  },
  {
    key: 'technology',
    name: 'Technology',
    format: joinNonEmpty,
  },
  {
    key: 'tpcFinishDate',
    name: 'TPC before',
    format: (value) => value,
  },
  {
    key: 'status',
    name: 'Status',
    format: (value) => (value ? statusLabel(value) : null),
  },
] as const satisfies {
  [K in keyof SavedCreativeFilters]: SummaryFieldConfig<K>;
}[keyof SavedCreativeFilters][];

export const formatSavedCreativeFiltersSummary = (
  filters: SavedCreativeFilters,
): SavedCreativeFilterSummaryRow[] => {
  const normalized = normalizeSavedCreativeFilters(filters);

  return SUMMARY_FIELDS.flatMap((field) => {
    const value = field.format(normalized[field.key] as never);
    return value ? [{ name: field.name, value }] : [];
  });
};
